import { useRef } from 'react'

import { IconSpin } from '@swaggerhub-portal/component-library'
import { useLoaderData, useOutletContext } from 'react-router'

import { PortalLandingPageLoader, loader as contentLoader } from './loader'
import { EditContentButtons } from '~/components/ConsumerEditContentButtons'
import { HeroSection } from '~/components/ConsumerHeroSection'
import { useDarkMode } from '~/components/DarkMode/hook'
import EmptyLandingPage from '~/components/EmptyLandingPage'
import { ErrorBoundaryComponent } from '~/components/ErrorBoundary'
import { ProductTile } from '~/components/ProductTile'
import { useConsumerBranding } from '~/hooks/useConsumerBranding'
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll'
import { useLandingPage } from '~/hooks/useLandingPage'
import { useShubOrgInfo } from '~/hooks/useShubOrgInfo'
import { useUser } from '~/hooks/useUser'
import { PortalRouteHandle } from '~/types/app'
import { LayoutType } from '~/types/layout'
import { RootOutletContext } from '~/types/portal'
import { consumerMeta } from '~/utils/shared/meta'
import { isAtLeastDesigner } from '~/utils/shared/roles'
import { getFullName } from '~/utils/shared/user'

export const loader = contentLoader

export const handle: PortalRouteHandle = {
  layout: {
    [LayoutType.DEFAULT]: {
      consumerTopBar: {
        search: {
          disabled: false
        }
      }
    }
  }
}

export const meta = consumerMeta

const LOADER_HREF = '?index'

export default function DocsOrgLandingPage() {
  const { isDark } = useDarkMode()

  const landingPageData = useLandingPage()
  const { isPreview } = useOutletContext<RootOutletContext>()

  const { accentColor, description, heroImage, illustrationImage, fontName, name } = useConsumerBranding()

  const { products, productsPage } = useLoaderData<PortalLandingPageLoader>()

  const { organizationRole } = useShubOrgInfo()

  const { user } = useUser()

  const infiniteScrollContainerRef = useRef<HTMLDivElement>(null)

  const { data, isFetching, fetchMore } = useInfiniteScroll(
    LOADER_HREF,
    products,
    productsPage?.totalElements ?? 0,
    infiniteScrollContainerRef.current,
  )

  const userFullName = getFullName(user)
  
  const isEmptyPage = !isFetching
    && Boolean(!data?.length)
    && (!description || description?.length === 0)
    && !heroImage
    && !illustrationImage
  
  if (isEmptyPage) {
    return (
      <EmptyLandingPage role={organizationRole} isDark={isDark()} portalName={name} />
    )
  }

  return (
    <div className="relative flex flex-col flex-1 overflow-auto dark:bg-neutral-95" ref={infiniteScrollContainerRef} onScroll={({ target }) => fetchMore(target as HTMLDivElement)}>
      {isAtLeastDesigner(organizationRole) && (
        <div className="absolute right-0 flex justify-end p-6 w-fit">
          <EditContentButtons contentUrl="/sp-admin/settings/general" />
        </div>
      )}
      <HeroSection
        heroImage={heroImage}
        illustrationImage={illustrationImage}
        pageDescription={description}
        fontFamily={fontName}
      />

      <section className="flex-1 bg-neutral-05 dark:bg-neutral-98">
        <div className="max-w-[80rem] grid lg:grid-cols-2 xl:grid-cols-3 px-6 md:px-9 xl:px-[3.75rem] py-6 md:py-9 xl:py-12 mx-auto gap-y-6 md:gap-y-9 lg:gap-x-9">
          {Boolean(data?.length) && data.map(product =>
            <ProductTile
              key={product.id}
              accentColor={accentColor}
              fontFamily={fontName}
              product={product}
              isPreview={isPreview}
              href={`/${product.slug}`}
              loggedIn={!!user?.email}
              portalName={landingPageData.name}
              userFullName={userFullName}
              responsive={true}
            />
          )}
        </div>
        {isFetching && <div className="flex items-center justify-center pb-20">
          <IconSpin width={48} height={48} className="mx-auto text-consumer-blue dark:text-dark-consumer-blue" />
        </div>}
      </section>
    </div>
  )
}

export const ErrorBoundary = ErrorBoundaryComponent